import React, { useContext, useState, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { Badge, Layout } from 'antd';
import {
  SearchOutlined,
  TeamOutlined,
  HistoryOutlined,
  CarOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import Logo from './humber-logo.png';
import { AuthContext } from '../../PrivateRoute/AuthProvider';
import useToast from '../../Global/outdatedVersionToast';
import useRegisterToken from '../../Global/useRegisterToken';
import { getSessionUserData } from '../../../libs/user';
import client from '../../../api/api';

const { Header } = Layout;

const CustomHeader = styled(Header)`
  && {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #1f1f1f !important;
    padding: 0 24px;
    height: auto !important;
    min-height: 80px;
    line-height: normal !important;
    border-bottom: 1px solid #333;
  }
`;

const LogoContainer = styled.div`
  width: 120px;
  display: flex;
  align-items: center;

  img {
    max-height: 50px;
    max-width: 100%;
    width: auto;
  }
`;

const NavItemsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DesktopMenu = styled.div`
  display: flex;
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.65);
  padding: 0 16px;
  height: 100%;
  transition: color 0.3s;

  &:hover {
    color: #fff;
  }

  .anticon {
    margin-right: 8px;
    font-size: 16px;
  }
`;

const MobileMenuButton = styled.div`
  display: none;
  font-size: 24px;
  color: white;
  cursor: pointer;
  
  @media (max-width: 992px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  width: 250px;
  background: #1f1f1f;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &.open {
    display: block;
  }

  a {
    display: flex;
    align-items: center;
    padding: 12px 24px;
    color: rgba(255, 255, 255, 0.65);
    
    &:hover {
      color: #fff;
      background: #1890ff;
    }

    .anticon {
      margin-right: 12px;
    }
  }
`;

const CarrierNav = () => {
  const { logout } = useContext(AuthContext);
  const { _id } = getSessionUserData()?.data || {};
  const {
    endpoints: { carrier },
  } = client;
  const [activeTripsCount, setActiveTripsCount] = useState(null);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;
  
    const fetchPendingActionsCount = async () => {
      try {
        const response = await client.endpoints.carrier.getPendingShipmentCount(_id);
        if (isMounted) {
          setActiveTripsCount(response.data.count || 0);
          retryCount = 0;
        }
      } catch (error) {
        console.error('Error fetching pending actions:', error);
        retryCount++;
        if (retryCount >= maxRetries && isMounted) {
          setActiveTripsCount(0); 
        }
      }
    };
    
    fetchPendingActionsCount();
    
    const interval = setInterval(fetchPendingActionsCount, 5000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [_id]);

  const logOut = async () => {
    await logout();
    await carrier.fbTokenLogOut(_id);
    setMobileMenuVisible(false);
  };

  const menuItems = [
    { 
      label: 'Buscar viajes', 
      key: 'search-trips', 
      icon: <SearchOutlined />, 
      path: '/carrier' 
    },
    { 
      label: 'Mi flota', 
      key: 'my-fleet', 
      icon: <TeamOutlined />, 
      path: '/carrier/my-fleet' 
    },
    { 
      label: 'Historial', 
      key: 'history', 
      icon: <HistoryOutlined />, 
      path: '/carrier/carrier-record' 
    },
    {
      label: (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          Viajes Activos
          <Badge 
            count={activeTripsCount} 
            showZero
            style={{ 
              marginLeft: '8px',
              backgroundColor: activeTripsCount > 0 ? '#ff3a30' : '#52c41a',
              boxShadow: '0 0 0 1px #1f1f1f',
              fontWeight: 'bold'
            }} 
          />
        </span>
      ), 
      key: 'active-trips', 
      icon: <CarOutlined />, 
      path: '/carrier/carrier-active' 
    },
    { 
      label: 'Cuenta corriente', 
      key: 'checking-account', 
      icon: <DollarOutlined />, 
      path: '/carrier/checking-account' 
    },
    { 
      label: 'Facturación', 
      key: 'billing', 
      icon: <FileTextOutlined />, 
      path: '/carrier/carrier-record-billing' 
    },
    { 
      label: 'Mi perfil', 
      key: 'profile', 
      icon: <UserOutlined />, 
      path: '/carrier/profile' 
    },
    { 
      label: 'Salir', 
      key: 'logout', 
      icon: <LogoutOutlined />, 
      path: '/', 
      action: logOut 
    },
  ];

  useToast();
  useRegisterToken();

  return (
    <CustomHeader>
      <LogoContainer>
        <Link to="/">
          <img src={Logo} alt="humber logo" />
        </Link>
      </LogoContainer>
      
      <NavItemsContainer>
        <DesktopMenu>
          {menuItems.map(item => (
            <NavItem 
              key={item.path}
              to={item.path}
              onClick={() => item.action && item.action()}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavItem>
          ))}
        </DesktopMenu>
        
        <MobileMenuButton onClick={() => setMobileMenuVisible(!mobileMenuVisible)}>
          <MenuOutlined />
        </MobileMenuButton>
      </NavItemsContainer>
      
      <MobileMenu className={mobileMenuVisible ? 'open' : ''}>
        {menuItems.map(item => (
          <Link 
            key={item.path}
            to={item.path}
            onClick={() => {
              if (item.action) item.action();
              setMobileMenuVisible(false);
            }}
          >
            {item.icon}
            <span>{item.label}</span>
          </Link>
        ))}
      </MobileMenu>
    </CustomHeader>
  );
};

export default withRouter(CarrierNav);
